import { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

interface ModalCellInputProps {
  open: boolean;
  onClose: () => void;
  onApply: (value: string) => void;
  disabled?: boolean;
}

const modalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
  outline: 'none',
};

const COMMAND_PATTERN = /^(\d+)#(.*)$/;

export function ModalCellInput({ open, onClose, onApply, disabled = false }: ModalCellInputProps) {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!open) setValue('');
  }, [open]);

  const isCommand = COMMAND_PATTERN.test(value.trim());
  const canApply = value.trim() !== '';

  const handleApply = () => {
    if (disabled || !canApply) return;
    onApply(value.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canApply && !disabled) handleApply();
    if (e.key === 'Escape') onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Stack sx={modalContentStyle} spacing={2}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797' }}>
            Enter Value
          </Typography>
          <IconButton size='small' onClick={onClose}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>
        <Divider />
        <TextField
          label='Value or command'
          size='small'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          fullWidth
          autoFocus
          placeholder='e.g.  100  or  5#100'
          onKeyDown={handleKeyDown}
        />
        <Typography variant='caption' color='text.secondary'>
          Enter a number to set this cell, or use{' '}
          <b>count#value</b> format (e.g. <b>5#100</b>) to repeat across columns.
          {isCommand && (
            <span style={{ color: '#42BB93' }}>&nbsp; Repeat command detected.</span>
          )}
        </Typography>
        {disabled && (
          <Typography variant='caption' sx={{ color: '#667085' }}>
            Apply is paused while the Budget Control Framework is being generated.
          </Typography>
        )}
        <Divider />
        <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
          <Button
            variant='main-table-panel-border'
            onClick={onClose}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant='main-table-panel'
            disabled={disabled || !canApply}
            onClick={handleApply}
            sx={{ textTransform: 'none' }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
